import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import type { ComponentProps } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Heading } from "@/components/ui/typography";
import { trpc } from "@/utils/trpc";

type SiteSelection = {
  id: string;
  name: string;
};

type SiteDetailsDialogProps = {
  selection: SiteSelection | null;
  onClose: () => void;
};

export default function SiteDetailsDialog({
  selection,
  onClose,
}: SiteDetailsDialogProps) {
  const detailsQuery = useQuery({
    ...trpc.admin.siteDetails.queryOptions({
      siteId: selection?.id ?? "",
    }),
    enabled: Boolean(selection?.id),
  });

  const site = detailsQuery.data?.site;
  const signInSummary = detailsQuery.data?.signInSummary;
  const managers = detailsQuery.data?.managers ?? [];
  const workerApprovals = detailsQuery.data?.workerApprovals ?? [];
  const openForms = detailsQuery.data?.openForms ?? [];

  const createdAtRelative = site?.createdAt
    ? formatDistanceToNow(new Date(site.createdAt), { addSuffix: true })
    : null;

  return (
    <Dialog
      open={Boolean(selection)}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto space-y-4">
        <DialogHeader>
          <DialogTitle>{selection?.name ?? "Site details"}</DialogTitle>
          <DialogDescription>
            {site?.address ?? "Review who is currently running this site."}
          </DialogDescription>
        </DialogHeader>

        {detailsQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        ) : detailsQuery.isError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            Failed to load site details. Please retry.
          </div>
        ) : detailsQuery.data ? (
          <>
            {site ? (
              <div className="rounded-md border bg-muted/30 p-3">
                <div className="flex items-center justify-between gap-3">
                  <Badge variant={siteStatusVariant(site.status)}>
                    {site.status}
                  </Badge>
                  {createdAtRelative ? (
                    <span className="text-xs text-muted-foreground">
                      Created {createdAtRelative}
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Timezone: {site.timezone}
                </p>
              </div>
            ) : null}

            <div className="grid gap-3 md:grid-cols-3">
              <SummaryStat
                label="Total sign-ins"
                value={String(signInSummary?.totalSignIns ?? 0)}
              />
              <SummaryStat
                label="Flagged sign-ins"
                value={String(signInSummary?.flaggedSignIns ?? 0)}
              />
              <SummaryStat
                label="Latest activity"
                value={
                  signInSummary?.lastSignInAt
                    ? formatDistanceToNow(
                        new Date(signInSummary.lastSignInAt),
                        {
                          addSuffix: true,
                        }
                      )
                    : "No sign-ins"
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <Heading variant="subsection" as="h3">
                Site managers
              </Heading>
              {managers.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No site managers assigned.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {managers.map((manager) => (
                    <li
                      key={manager.id}
                      className="rounded-md border bg-background px-3 py-2"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {manager.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {manager.email}
                          </p>
                        </div>
                        <Badge variant="outline">{manager.status}</Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Heading variant="subsection" as="h3">
                Worker approvals
              </Heading>
              {workerApprovals.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No worker approvals recorded for this site.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {workerApprovals.map((approval) => (
                    <li
                      key={approval.id}
                      className="rounded-md border bg-background px-3 py-2"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {approval.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {approval.email}
                          </p>
                        </div>
                        <Badge variant="secondary">{approval.status}</Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Heading variant="subsection" as="h3">
                Open forms
              </Heading>
              {openForms.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No open forms at this site.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {openForms.map((form) => {
                    const updatedAt = form.updatedAt
                      ? new Date(form.updatedAt)
                      : null;
                    const relative = updatedAt
                      ? formatDistanceToNow(updatedAt, { addSuffix: true })
                      : null;

                    return (
                      <li
                        key={form.id}
                        className="rounded-md border bg-background px-3 py-2"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {form.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {relative
                                ? `Updated ${relative}`
                                : "Awaiting action"}
                            </p>
                          </div>
                          <Badge variant={formStatusVariant(form.status)}>
                            {form.status}
                          </Badge>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background px-3 py-3">
      <p className="text-xs uppercase text-muted-foreground">{label}</p>
      <p className="mt-2 text-sm font-semibold text-foreground">{value}</p>
    </div>
  );
}

function siteStatusVariant(status: string) {
  const variant: ComponentProps<typeof Badge>["variant"] =
    status === "active"
      ? "approved"
      : status === "planned"
        ? "secondary"
        : status === "completed"
          ? "ghost"
          : "outline";

  return variant;
}

function formStatusVariant(status: string) {
  const badgeStatuses = new Set([
    "draft",
    "pending_review",
    "approved",
    "rejected",
    "closed",
  ]);

  if (badgeStatuses.has(status)) {
    return status as ComponentProps<typeof Badge>["variant"];
  }

  return "outline";
}
